import { useState } from 'react';

import { Badge } from '@/components/Badge/Badge';
import { Button } from '@/components/Button/Button';
import { BarList } from '@/components/Chart/BarList';
import { DonutChart } from '@/components/Chart/DonutChart';
import { type AppUsageReport } from '@/features/reports/api';
import {
    latestVersion,
    REASON_VARIANT,
    reasonLine,
    reasonTag,
} from '@/features/reports/app/labels';
import { APP_USAGE_COPY as COPY } from '@/features/reports/copy';
import { ReportCard } from '@/features/reports/parts';

/*
  App usage's three cards (ALS-160), side by side from 1024 and stacked under it: the versions
  people last opened, the platforms they opened them on, and the reasons a stylist would call.
*/

/** People by the version they last opened, latest release first, latest marked. */
export function VersionsCard({ report }: { report: AppUsageReport }) {
    const versions = report.versions;
    const latest = latestVersion(report.latestReleases) ?? COPY.figures.onLatest.theLatest;
    const current = versions.length > 0 && versions.every((v) => v.isLatest);
    return (
        <ReportCard title={COPY.versions.title} subtitle={COPY.versions.subtitle}>
            <BarList
                items={versions.map((v) => {
                    const label = v.version ?? COPY.versions.unknown;
                    return {
                        label: v.isLatest ? COPY.versions.latest(label) : label,
                        value: v.people,
                    };
                })}
                format={String}
                emptyNote={COPY.nobodyOpened}
            />
            {versions.length > 0 && report.latestReleases.length > 0 ? (
                <p className="text-muted-foreground mt-3 text-xs">
                    {current ? COPY.versions.current : COPY.versions.behind(latest)}
                </p>
            ) : null}
        </ReportCard>
    );
}

/** People by the platform of the session they last used, each with their share, as the prototype's legend. */
export function DevicesCard({ report }: { report: AppUsageReport }) {
    const total = report.devices.reduce((sum, d) => sum + d.people, 0);
    return (
        <ReportCard title={COPY.devices.title} subtitle={COPY.devices.subtitle}>
            <DonutChart
                title={COPY.devices.title}
                items={report.devices.map((d) => ({ label: d.platform, value: d.people }))}
                format={(people) => COPY.devices.legend(people, Math.round((people / total) * 100))}
                emptyNote={COPY.nobodyOpened}
            />
        </ReportCard>
    );
}

/** How many reasons Worth a call shows before Show all, so a long list does not stretch its row. */
export const CALLS_PREVIEW = 8;

/**
 * One row per reason a stylist would act, name and tag on one line and the reason under them, so
 * the rows line up whatever the tag says (Franka, 15 Sep 2026). Folded to the first eight, so a
 * busy window does not stretch the three cards' row to the length of the list.
 */
export function WorthACallCard({ report }: { report: AppUsageReport }) {
    const [expanded, setExpanded] = useState(false);
    const rows = report.worthACall;
    const folds = rows.length > CALLS_PREVIEW;
    const shown = folds && !expanded ? rows.slice(0, CALLS_PREVIEW) : rows;
    return (
        <ReportCard
            title={COPY.calls.title}
            subtitle={COPY.people(rows.length)}
            actions={
                folds ? (
                    <Button variant="outline" size="sm" onClick={() => setExpanded((o) => !o)}>
                        {expanded
                            ? COPY.calls.showFirst(CALLS_PREVIEW)
                            : COPY.calls.showAll(rows.length)}
                    </Button>
                ) : undefined
            }
        >
            {rows.length === 0 ? (
                <p className="text-muted-foreground py-4 text-sm">{COPY.calls.empty}</p>
            ) : (
                <ul className="divide-border flex flex-col divide-y">
                    {shown.map((row) => (
                        <li
                            key={`${row.userId}-${row.reason}`}
                            className="py-2.5 first:pt-0 last:pb-0"
                        >
                            <p className="mb-1 flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium">{row.name}</span>
                                <Badge variant={REASON_VARIANT[row.reason]} size="sm">
                                    {reasonTag(row, report.days)}
                                </Badge>
                            </p>
                            <p className="text-muted-foreground text-xs">
                                {reasonLine(row, report)}
                            </p>
                        </li>
                    ))}
                </ul>
            )}
        </ReportCard>
    );
}
